import Vue from 'vue';
import constants from '../../../plugins/constants';
const { BOT_NAME } = constants;

import tasksModule from './tasks';
import dashboardModule from './dashboard';
import taskDueNotificationsModule from './taskDueNotifications';
import tmReconciliationModule from './tmReconciliation';
import transactionMatchingModule from './transactionMatching';
import informationManagerModule from './informationManager';
import trialBalanceModule from './trialBalance';
import projectModule from './project';

function buildCycleItems(id) {
  const items = [
    { name: 'Dashboard', href: `/financialclose/project/dashboard/${id}`, icon: '/img/navicon/Dashboard.svg' },
    { name: 'Tasklist', href: `/financialclose/project/checklist/${id}`, icon: '/img/navicon/TaskList.svg' },
    { name: 'Information Manager', href: `/financialclose/project/informationmanager/${id}`, icon: '/img/navicon/InfoManager.svg' },
    { name: 'Trial Balance', href: `/financialclose/project/trialbalance/${id}`, icon: '/img/navicon/TrialBalance.svg', class: 'noTransform' },
    { name: 'Cycle Settings',
      icon: '/img/navicon/CycleSetting.svg',
      items: [
        { name: 'Users', href: `/financialclose/project/users/${id}`, icon: '' },
        { name: 'Threshold', href: `/financialclose/project/threshold/${id}`, icon: '' },
      ]
    },
    {
      name: BOT_NAME,
      icon: '/img/navicon/AutomationAgent.svg',
      items: [
        { name: 'Transaction Matching', href: `/financialclose/automationtransactionmatching/${id}`, icon: '' },
        { name: 'Risk Management', href: `/financialclose/automation/riskmanagementhome/${id}`, icon: '' },
        { name: 'Lease Schedule', href: `/financialclose/automationlease/${id}`, icon: '' },
        { name: 'Borrowing Schedule', href: `/financialclose/automationborrowingschedule/${id}`, icon: '' },
        { name: 'FS Preparation', href: `/financialclose/manage/financialstatementpreparation/${id}`, icon: '', class: 'noTransform' }
      ]
    }
  ];
  // Deduplicate by href to avoid duplicates if rebuild called multiple times
  const seen = new Set();
  return items.filter(it => { const key = it.href || it.name; if (seen.has(key)) return false; seen.add(key); return true; });
}

export default {
  modules: {
    tasks: tasksModule,
    dashboard: dashboardModule,
    taskDueNotifications: taskDueNotificationsModule,
    tmReconciliation: tmReconciliationModule,
    transactionMatching: transactionMatchingModule,
    informationManager: informationManagerModule,
    trialBalance: trialBalanceModule,
    project: projectModule,
  },
  state: {
    CycleList: [],
    menu: [
      { name: 'Home', icon: '/img/navicon/Home.svg', href: '/financialclose/home', right: 'financialclose_project' },
      { name: 'Shortcuts', icon: '/img/navicon/Shortcuts.svg', items: [] },
      { name: 'Cycles', icon: '/img/navicon/Cycles.svg', items: [] },
      {
        name: 'Masters',
        icon: '/img/navicon/FCMasters.svg',
        right: 'financialclose_master_checklist',
        items: [
          { name: 'Chart of Accounts', href: '/financialclose/manage/chartofaccount', right: 'financialclose_master_chartofaccount', icon: '' },
          { name: 'Tasklist', href: '/financialclose/manage/checklist', right: 'financialclose_master_checklist', icon: '' },
          { name: 'Information Manager', href: '/financialclose/manage/informationmanager', right: 'financialclose_master_informationmanager', icon: '' },
          { name: 'Location', href: '/financialclose/manage/location', right: 'financialclose_master_checklist', icon: '', edit: true },
          { name: 'Threshold', href: '/financialclose/manage/threshold', right: 'financialclose_master_checklist', icon: '' },
          { name: "Email Notifications", href: "/financialclose/TaskDueNotifications", right: 'financialclose_project', icon: '' }
        ]
      }
    ]
  },
  mutations: {
    resetCycles(state) {
      state.CycleList = [];
      const cycles = state.menu.find(m => m.name === 'Cycles');
      if (cycles) cycles.items = [];
    },
    setNewList(state, list) {
      if (!Array.isArray(list)) return;
      state.CycleList = list.map(c => ({ name: c?.description, _id: c?._id, items: buildCycleItems(c?._id) }));
      const cycles = state.menu.find(m => m.name === 'Cycles');
      if (cycles) cycles.items = state.CycleList;
    },
    addItemToCycleList(state, item) {
      if (!item || !item._id) return;
      const idx = state.CycleList.findIndex(c => c._id === item._id);
      const cycleObj = { name: item.description, _id: item._id, items: buildCycleItems(item._id) };
      if (idx > -1) {
        // Replace existing cycle with updated data
        state.CycleList.splice(idx, 1, cycleObj);
      } else {
        // Requirement: if any cycles exist already, replace the whole list (do not append)
        state.CycleList = [cycleObj];
      }
      const cycles = state.menu.find(m => m.name === 'Cycles');
      if (cycles) cycles.items = state.CycleList;
    }
  },
    actions:{

      /**
       * Fetch and set cycle menu from route path on page refresh
       * Extracts project ID from the current route and populates the cycle navigation
       * @param {Object} context - Vuex action context
       * @param {Object} params - Action parameters
       * @param {string} params.routePath - Current route path to extract project ID from
       * @returns {Promise<boolean>} - True if cycle was successfully set, false otherwise
       */
      /**
       * Fetch and set cycle menu from route path on page refresh
       * Extracts project ID from the current route and populates the cycle navigation
       * @param {Object} context - Vuex action context
       * @param {Object} params - Action parameters
       * @param {string} params.routePath - Current route path to extract project ID from
       * @returns {Promise<boolean>} - True if cycle was successfully set, false otherwise
       */
      /**
       * Fetch activity log entries for a given resource.
       * @param {Object} context - Vuex action context
       * @param {Object} params - { resourceId, type }
       * @returns {Promise<{ data: Array, unique_type: Array }>}
       */
      async fetchActivityLog({ commit }, { resourceId, type }) {
        try {
          const url = `/v2/activitylogs/get/${resourceId}`;
          const payload = type && type.length > 0 ? { data: { type } } : {};
          const response = await Vue.axios.post(url, payload);
          if (response.data.status === 'success') {
            return {
              data: response.data.data || [],
              unique_type: response.data.unique_type || [],
            };
          } else {
            throw new Error('Error loading activity log');
          }
        } catch (error) {
          console.error('[financialclose/store]: fetchActivityLog error', error);
          commit(
            'sbError',
            error?.response?.data?.message || error?.message || 'Failed to load activity log',
            { root: true },
          );
          throw error;
        }
      },

      async fetchAndSetCycleFromRoute({ commit, state }, { routePath }) {
        const cycleRoutePatterns = [
          /\/financialclose\/project\/[^/]+\/([a-f0-9]{24})/i,
          /\/financialclose\/automation[^/]*\/([a-f0-9]{24})/i,
          /\/financialclose\/manage\/financialstatementpreparation\/([a-f0-9]{24})/i
        ];

        let projectId = null;
        for (const pattern of cycleRoutePatterns) {
          const match = routePath?.match(pattern);
          if (match && match[1]) {
            projectId = match[1];
            break;
          }
        }

        if (!projectId) return false;

        // Check if this cycle is already loaded
        const existingCycle = state.CycleList.find(c => c._id === projectId);
        if (existingCycle) return true;

        try {
          const response = await Vue.axios.post(`/v3/financialclose/analysis/getdetails/${projectId}`);
          
          if (response?.data?.status === 'success' && response?.data?.data) {
            // API returns data as array - access first element
            const dataArray = response.data.data;
            const cycleData = Array.isArray(dataArray) ? dataArray[0] : dataArray;
            const metadata = cycleData?.metadata || cycleData;
            
            // Try to get _id and description from various possible locations
            const projectIdFromResponse = metadata?._id || cycleData?._id || cycleData?.project?._id;
            const description = metadata?.description || cycleData?.description || cycleData?.project?.description || 'Cycle';
            
            if (projectIdFromResponse) {
              commit('addItemToCycleList', {
                _id: projectIdFromResponse,
                description: description
              });
              return true;
            }
          }
          return false;
        } catch (err) {
          console.error('[financialclose/store]: fetchAndSetCycleFromRoute error', err);
          return false;
        }
      },
    },
  getters: {
    getMenu: state => state.menu
  }
};
